import React, { useState, useEffect } from 'react';
import SnapshotSelector from '../components/SnapshotSelector';
import { apiService, RankingHealth, ApiAccessStat } from '../services/api';
import './RankingHealthCheckPage.css';

// Mapa de nomes amigáveis para os steps do pipeline
const PIPELINE_STEP_NAMES: { [key: string]: string } = {
  'download_promoplan': '📥 Download Promoplan',
  'invoice_averages_calculation': '📊 Médias de Faturas',
  'hourly_sales_aggregation': '⏱️ Vendas Horárias',
  'price_timeline_generation': '📈 Timeline de Preços',
  'daily_stock_aggregation': '📦 Stocks Diários',
  'ranking_calculation': '🏆 Cálculo de Ranking'
};

const RankingHealthCheckPage: React.FC = () => {
  const [selectedSnapshot, setSelectedSnapshot] = useState<string | null>(null);
  const [health, setHealth] = useState<RankingHealth | null>(null);
  const [apiStats, setApiStats] = useState<ApiAccessStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSnapshot) {
      loadHealth(selectedSnapshot);
    } else {
      // Load latest by default
      loadHealth();
    }
    // Also load audit data
    loadAuditData();
  }, [selectedSnapshot]);

  const loadHealth = async (timestamp?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getRankingHealth(timestamp);
      setHealth(data);
    } catch (err) {
      setError('Erro ao carregar dados de saúde do ranking');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditData = async () => {
    try {
      const apiStats = await apiService.getApiAccessStats(24);
      setApiStats(apiStats);
    } catch (err) {
      console.error('Erro ao carregar dados de auditoria:', err);
    }
  };

  const getHealthStatusColor = (coverage: number) => {
    if (coverage >= 90) return 'success';
    if (coverage >= 80) return 'warning';
    return 'danger';
  };

  return (
    <div className="container-fluid mt-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <h1 className="display-5">
            <span className="me-2">🏥</span>
            Ranking Health Check
          </h1>
          <p className="lead text-muted">Diagnóstico completo da saúde do snapshot de ranking</p>
        </div>
      </div>

      {/* Snapshot Selector */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Selecionar Snapshot</h5>
            </div>
            <div className="card-body">
              <SnapshotSelector
                selectedSnapshot={selectedSnapshot}
                onSnapshotChange={setSelectedSnapshot}
              />
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="row">
          <div className="col-12">
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Carregando dados de saúde...</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        </div>
      )}

      {health && !loading && (
        <>
          {/* Snapshot Info */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card bg-light">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <p className="mb-0">
                        <strong>Timestamp:</strong> {new Date(health.snapshot_info.timestamp).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' })}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-0">
                        <strong>Freshness:</strong>{' '}
                        <span className={`badge bg-${
                          health.snapshot_info.freshness_hours < 0
                            ? 'danger'
                            : health.snapshot_info.freshness_hours < 1
                            ? 'success'
                            : health.snapshot_info.freshness_hours < 24
                            ? 'info'
                            : 'warning'
                        }`}>
                          {health.snapshot_info.freshness_hours >= 0
                            ? `${health.snapshot_info.freshness_hours.toFixed(1)}h atrás`
                            : `${Math.abs(health.snapshot_info.freshness_hours).toFixed(1)}h no futuro ⚠️`
                          }
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline Status - Usando dados de auditoria */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">⚙️ Status do Pipeline ETL</h5>
                </div>
                <div className="card-body">
                  {health.pipeline_audit && health.pipeline_audit.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Script</th>
                            <th>Última Execução</th>
                            <th>Há</th>
                            <th>Status</th>
                            <th>Duração</th>
                            <th>Registos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {health.pipeline_audit.map((step, idx) => (
                            <tr key={idx} className={step.status === 'success' ? '' : 'table-warning'}>
                              <td><strong>{PIPELINE_STEP_NAMES[step.pipeline_step] || step.pipeline_step}</strong></td>
                              <td>
                                {step.last_execution
                                  ? new Date(step.last_execution).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' })
                                  : 'N/A'
                                }
                              </td>
                              <td>
                                {step.hours_ago !== null ? `${step.hours_ago.toFixed(1)}h` : 'N/A'}
                              </td>
                              <td>
                                <span className={`badge bg-${
                                  step.status === 'success'
                                    ? (step.hours_ago < 24 ? 'success' : step.hours_ago < 48 ? 'warning' : 'danger')
                                    : 'danger'
                                }`}>
                                  {step.status === 'success'
                                    ? (step.hours_ago < 24 ? '✓ Atualizado' : step.hours_ago < 48 ? 'Desatualizado' : 'Crítico')
                                    : '✗ Failed'
                                  }
                                </span>
                              </td>
                              <td>
                                {step.duration_seconds !== null ? `${step.duration_seconds.toFixed(1)}s` : 'N/A'}
                              </td>
                              <td>
                                {step.records_processed !== null ? step.records_processed.toLocaleString() : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-info">
                      <p className="mb-0">Carregando dados de auditoria do pipeline...</p>
                    </div>
                  )}

                  {/* Sales Synchronization Info */}
                  {health.sales_sync && (
                    <div className="mt-4 pt-4 border-top">
                      <h6 className="mb-3">📊 Sincronização de Vendas</h6>
                      <div className="row mb-3">
                        <div className="col-12">
                          <p className="mb-1">
                            <strong>Última fatura de hoje:</strong>{' '}
                            {health.sales_sync.last_invoice_today
                              ? new Date(health.sales_sync.last_invoice_today).toLocaleString('pt-PT', {
                                  timeZone: 'Europe/Lisbon',
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })
                              : 'Sem vendas hoje'}
                          </p>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-4 mb-2">
                          <div className="card bg-light">
                            <div className="card-body p-3">
                              <small className="text-muted d-block mb-1">Faturação Hoje</small>
                              <h5 className="mb-0">
                                {new Intl.NumberFormat('pt-PT', {
                                  style: 'currency',
                                  currency: 'EUR'
                                }).format(health.sales_sync.revenue_today || 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4 mb-2">
                          <div className="card bg-light">
                            <div className="card-body p-3">
                              <small className="text-muted d-block mb-1">Faturação Ontem</small>
                              <h5 className="mb-0">
                                {new Intl.NumberFormat('pt-PT', {
                                  style: 'currency',
                                  currency: 'EUR'
                                }).format(health.sales_sync.revenue_yesterday || 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4 mb-2">
                          <div className="card bg-light">
                            <div className="card-body p-3">
                              <small className="text-muted d-block mb-1">Média 7 Dias</small>
                              <h5 className="mb-0">
                                {new Intl.NumberFormat('pt-PT', {
                                  style: 'currency',
                                  currency: 'EUR'
                                }).format(health.sales_sync.avg_revenue_7d || 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Data Quality */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">📊 Cobertura de Dados</h5>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <div className="health-metric-box">
                        <div className="health-metric-value">{health.data_quality.total_products.toLocaleString()}</div>
                        <div className="health-metric-label">Produtos Ranked</div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="health-metric-box">
                        <div className="health-metric-value">{health.data_quality.products_online_sap.toLocaleString()}</div>
                        <div className="health-metric-label">Online (SAP)</div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="health-metric-box">
                        <div className={`health-metric-value text-${getHealthStatusColor(health.data_quality.coverage_pct)}`}>
                          {health.data_quality.coverage_pct.toFixed(1)}%
                        </div>
                        <div className="health-metric-label">Cobertura</div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="health-metric-box">
                        <div className="health-metric-value">{health.data_quality.products_with_sales_30d.toLocaleString()}</div>
                        <div className="health-metric-label">Com Vendas (30d)</div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <tbody>
                        <tr>
                          <td><strong>Com preço válido</strong></td>
                          <td>
                            <span className="badge bg-success me-2">{health.data_quality.products_with_price.toLocaleString()}</span>
                            <span className="text-muted">({((health.data_quality.products_with_price / health.data_quality.products_online_sap) * 100).toFixed(1)}%)</span>
                          </td>
                        </tr>
                        <tr className="table-warning">
                          <td><strong>❌ SEM preço</strong></td>
                          <td>
                            <span className="badge bg-warning text-dark me-2">{health.data_quality.products_no_price.toLocaleString()}</span>
                            <span className="text-muted">({((health.data_quality.products_no_price / health.data_quality.products_online_sap) * 100).toFixed(1)}%)</span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Sem stock</strong></td>
                          <td>
                            <span className="badge bg-info me-2">{health.data_quality.products_no_stock.toLocaleString()}</span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Sem categoria</strong></td>
                          <td>
                            <span className="badge bg-secondary me-2">{health.data_quality.products_no_category.toLocaleString()}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Velocity Distribution */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">🏆 Distribuição por Velocidade (V2.0)</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Categoria</th>
                          <th className="text-end">Produtos</th>
                          <th className="text-end">Avg Score</th>
                          <th className="text-end">Penalização</th>
                          <th className="text-end">Min-Max Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {health.velocity_distribution.map((v) => (
                          <tr key={v.category}>
                            <td>
                              <span className="badge bg-primary">{v.category}</span>
                            </td>
                            <td className="text-end">{v.count.toLocaleString()}</td>
                            <td className="text-end">
                              <strong>{v.avg_score.toFixed(4)}</strong>
                            </td>
                            <td className="text-end">
                              <span className={`badge ${v.avg_penalty > 0.8 ? 'bg-success' : v.avg_penalty > 0.5 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                {v.avg_penalty.toFixed(3)}
                              </span>
                            </td>
                            <td className="text-end text-muted">
                              {v.min_score.toFixed(4)} - {v.max_score.toFixed(4)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Alerts */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">⚠️ Alertas e Anomalias</h5>
                </div>
                <div className="card-body">
                  {health.alerts.critical.length === 0 &&
                    health.alerts.warnings.length === 0 &&
                    health.alerts.info.length === 0 ? (
                    <div className="alert alert-success mb-0">
                      <strong>✅ Tudo bem!</strong> Nenhum alerta ou anomalia detectada.
                    </div>
                  ) : (
                    <>
                      {health.alerts.critical.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-danger">🔴 Críticos ({health.alerts.critical.length})</h6>
                          {health.alerts.critical.map((alert, idx) => (
                            <div key={idx} className="alert alert-danger mb-2">
                              <strong>{alert.message}</strong>
                              <span className="badge bg-danger ms-2">{alert.count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {health.alerts.warnings.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-warning">🟡 Avisos ({health.alerts.warnings.length})</h6>
                          {health.alerts.warnings.map((alert, idx) => (
                            <div key={idx} className="alert alert-warning mb-2">
                              <strong>{alert.message}</strong>
                              <span className="badge bg-warning text-dark ms-2">{alert.count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {health.alerts.info.length > 0 && (
                        <div>
                          <h6 className="text-info">🔵 Informação ({health.alerts.info.length})</h6>
                          {health.alerts.info.map((alert, idx) => (
                            <div key={idx} className="alert alert-info mb-2">
                              <strong>{alert.message}</strong>
                              <span className="badge bg-info ms-2">{alert.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stock Analysis */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">📦 Análise de Stock</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Range</th>
                          <th className="text-end">Produtos</th>
                          <th className="text-end">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {health.stock_analysis.distribution.map((stock, idx) => {
                          const total = health.stock_analysis.distribution.reduce((sum, s) => sum + s.count, 0);
                          const pct = ((stock.count / total) * 100).toFixed(1);
                          return (
                            <tr key={idx}>
                              <td>
                                <strong>
                                  {stock.range === '0' && '0 unidades'}
                                  {stock.range === '1-10' && '1-10 unidades'}
                                  {stock.range === '11-50' && '11-50 unidades'}
                                  {stock.range === '51+' && '51+ unidades'}
                                </strong>
                              </td>
                              <td className="text-end">{stock.count.toLocaleString()}</td>
                              <td className="text-end">{pct}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Penalty Analysis */}
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">🔧 Análise de Penalizações</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Fortemente Penalizados</strong></td>
                          <td className="text-end">
                            <span className="badge bg-danger">
                              {health.penalty_analysis.heavily_penalized_count}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Média de Penalização</strong></td>
                          <td className="text-end">{health.penalty_analysis.avg_penalty.toFixed(3)}</td>
                        </tr>
                        <tr>
                          <td><strong>Desvio Padrão</strong></td>
                          <td className="text-end">{health.penalty_analysis.stddev_penalty.toFixed(4)}</td>
                        </tr>
                        <tr className="table-light">
                          <td colSpan={2}><strong>Percentis</strong></td>
                        </tr>
                        <tr>
                          <td>P25</td>
                          <td className="text-end">{health.penalty_analysis.p25.toFixed(3)}</td>
                        </tr>
                        <tr>
                          <td>P50 (Mediana)</td>
                          <td className="text-end">{health.penalty_analysis.p50.toFixed(3)}</td>
                        </tr>
                        <tr>
                          <td>P75</td>
                          <td className="text-end">{health.penalty_analysis.p75.toFixed(3)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Top Categories and Brands */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">🏷️ Top 10 Categorias</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Categoria</th>
                          <th className="text-end">Produtos</th>
                          <th className="text-end">Avg Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {health.top_categories.map((cat, idx) => (
                          <tr key={idx}>
                            <td>{cat.categoria}</td>
                            <td className="text-end">{cat.count.toLocaleString()}</td>
                            <td className="text-end">{cat.avg_score.toFixed(4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">🏷️ Top 10 Marcas</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Marca</th>
                          <th className="text-end">Produtos</th>
                          <th className="text-end">Avg Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {health.top_brands.map((brand, idx) => (
                          <tr key={idx}>
                            <td>{brand.marca}</td>
                            <td className="text-end">{brand.count.toLocaleString()}</td>
                            <td className="text-end">{brand.avg_score.toFixed(4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Auditoria - API Access Stats */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">📊 Acessos à API (Últimas 24h)</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Endpoint</th>
                          <th className="text-end">Nº Acessos</th>
                          <th className="text-end">Taxa Sucesso</th>
                          <th className="text-end">Tempo Médio (ms)</th>
                          <th>Último Acesso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiStats.length > 0 ? (
                          apiStats.map((stat, idx) => (
                            <tr key={idx}>
                              <td><code>{stat.endpoint}</code></td>
                              <td className="text-end">
                                <span className="badge bg-info">{stat.total_requests}</span>
                              </td>
                              <td className="text-end">
                                <span className={`badge bg-${stat.success_rate_pct >= 90 ? 'success' : stat.success_rate_pct >= 70 ? 'warning' : 'danger'}`}>
                                  {stat.success_rate_pct.toFixed(1)}%
                                </span>
                              </td>
                              <td className="text-end">{stat.avg_response_time_ms.toFixed(0)}</td>
                              <td>{stat.minutes_ago !== null ? `${stat.minutes_ago.toFixed(0)}m atrás` : 'N/A'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">Carregando dados de acessos...</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stock Snapshots Dates */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">📅 Últimos Snapshots de Stock (Tabela RAW)</h5>
                </div>
                <div className="card-body">
                  {health.stock_snapshots_dates && health.stock_snapshots_dates.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Data do Snapshot</th>
                            <th>Há</th>
                          </tr>
                        </thead>
                        <tbody>
                          {health.stock_snapshots_dates.map((snapshot, idx) => {
                            const snapshotDate = new Date(snapshot.date);
                            const now = new Date();
                            const daysAgo = Math.floor((now.getTime() - snapshotDate.getTime()) / (1000 * 60 * 60 * 24));

                            return (
                              <tr key={idx}>
                                <td>
                                  <strong>
                                    {snapshotDate.toLocaleDateString('pt-PT', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })}
                                  </strong>
                                </td>
                                <td>
                                  <span className={`badge bg-${daysAgo === 0 ? 'success' : daysAgo === 1 ? 'info' : 'secondary'}`}>
                                    {daysAgo === 0 ? 'Hoje' : daysAgo === 1 ? 'Ontem' : `${daysAgo} dias atrás`}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-info mb-0">
                      <p className="mb-0">Sem dados de snapshots de stock disponíveis.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RankingHealthCheckPage;
